/** Debtor country options for demand records. */

export const DEBTOR_COUNTRIES = Object.freeze([
  { value: 'US', label: 'USA' },
  { value: 'GB', label: 'UK' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
]);

const COUNTRY_BY_ALIAS = new Map([
  ['US', 'US'],
  ['USA', 'US'],
  ['UNITED STATES', 'US'],
  ['UNITED STATES OF AMERICA', 'US'],
  ['GB', 'GB'],
  ['UK', 'GB'],
  ['UNITED KINGDOM', 'GB'],
  ['GREAT BRITAIN', 'GB'],
  ['CA', 'CA'],
  ['CAN', 'CA'],
  ['CANADA', 'CA'],
  ['AU', 'AU'],
  ['AUS', 'AU'],
  ['AUSTRALIA', 'AU'],
  ['NZ', 'NZ'],
  ['NZL', 'NZ'],
  ['NEW ZEALAND', 'NZ'],
  ['IE', 'IE'],
  ['IRL', 'IE'],
  ['IRELAND', 'IE'],
]);

const CA_PROVINCES = new Set([
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

/** @returns {'US'|'GB'|'CA'|'AU'|'NZ'|'IE'|null} */
export function coerceDebtorCountry(value) {
  const raw = String(value || '')
    .trim()
    .toUpperCase();
  if (!raw) return null;
  return COUNTRY_BY_ALIAS.get(raw) || null;
}

export function isDebtorCountry(value) {
  return coerceDebtorCountry(value) != null;
}

/**
 * Normalize to a supported country code. Defaults to USA when unset and no hints resolve.
 * @param {string} [value]
 * @param {{ postalCode?: string, state?: string }} [hints]
 */
export function normalizeDebtorCountry(value, hints = {}) {
  const explicit = coerceDebtorCountry(value);
  if (explicit) return explicit;

  const postal = String(hints.postalCode || '')
    .replace(/\s+/g, '')
    .toUpperCase();
  if (/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(postal)) {
    return 'CA';
  }
  if (/^\d{5}(?:\d{4})?$/.test(postal)) return 'US';
  if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/i.test(postal)) return 'GB';
  if (/^[A-Z]\d{2}[A-Z0-9]{4}$/i.test(postal)) return 'IE';
  if (/^\d{4}$/.test(postal)) {
    // AU and NZ both use 4-digit postcodes; leave default US unless region hints NZ/AU.
  }

  const region = String(hints.state || '')
    .trim()
    .toUpperCase();
  if (region && CA_PROVINCES.has(region)) return 'CA';

  return 'US';
}

export function debtorCountryLabel(value) {
  const code = normalizeDebtorCountry(value);
  const match = DEBTOR_COUNTRIES.find((item) => item.value === code);
  return match?.label || 'USA';
}

/** Region field label (state / province / county). */
export function debtorRegionLabel(country) {
  switch (normalizeDebtorCountry(country)) {
    case 'CA':
      return 'Debtor province';
    case 'GB':
    case 'IE':
      return 'Debtor county / region';
    case 'AU':
      return 'Debtor state / territory';
    case 'NZ':
      return 'Debtor region';
    default:
      return 'Debtor state';
  }
}

export function debtorRegionPlaceholder(country) {
  switch (normalizeDebtorCountry(country)) {
    case 'CA':
      return 'ON';
    case 'GB':
      return 'Greater London';
    case 'AU':
      return 'NSW';
    case 'NZ':
      return 'Wellington';
    case 'IE':
      return 'Dublin';
    default:
      return 'TX';
  }
}

export function debtorPostalPlaceholder(country) {
  switch (normalizeDebtorCountry(country)) {
    case 'CA':
      return 'A1A 1A1';
    case 'GB':
      return 'SW1A 1AA';
    case 'AU':
      return '2000';
    case 'NZ':
      return '6011';
    case 'IE':
      return 'D02 AF30';
    default:
      return '78701';
  }
}

/** US/CA stay at 2-letter codes; other countries allow longer regions. */
export function debtorRegionMaxLength(country) {
  const code = normalizeDebtorCountry(country);
  return code === 'US' || code === 'CA' ? 2 : 64;
}

/** ISO region codes for Google Places autocomplete (lowercase). */
export const DEBTOR_AUTOCOMPLETE_REGION_CODES = Object.freeze([
  'us',
  'gb',
  'ca',
  'au',
  'nz',
  'ie',
]);

/**
 * Region codes for Places autocomplete.
 * Always includes all supported countries so street search can autofill Country.
 * When a country is selected, it is listed first.
 */
export function autocompleteRegionCodes(country) {
  const code = coerceDebtorCountry(country)?.toLowerCase();
  if (code && DEBTOR_AUTOCOMPLETE_REGION_CODES.includes(code)) {
    return [code, ...DEBTOR_AUTOCOMPLETE_REGION_CODES.filter((item) => item !== code)];
  }
  return [...DEBTOR_AUTOCOMPLETE_REGION_CODES];
}
